// EVE-Memory API-Client. V3-Routen (Phase 2 Worker implementiert):
// - GET    /eve/memories?limit=100
// - POST   /eve/memories
// - DELETE /eve/memories/:id
// - DELETE /eve/memories
// - GET    /eve/ras
// - POST   /eve/ras
// - DELETE /eve/ras/:id
// - DELETE /eve/ras
// - GET    /eve/ras/global
// - POST   /eve/ras/global   (owner-only)
// - DELETE /eve/ras/global/:id  (owner-only)

import { apiFetch } from './api';
import type { GlobalRasRule, MemoryEntry, RasRule } from '@/types/eve-memory';

// ── Memory CRUD ──────────────────────────────────────────────────────────────

export async function listMemories(accessToken: string, limit = 100): Promise<MemoryEntry[]> {
  return apiFetch<MemoryEntry[]>(`/eve/memories?limit=${limit}`, { accessToken });
}

export interface AddMemoryInput {
  fact: string;
  /** Override für `date` (V1: `DD.MM.YY`). Default: heute. */
  date?: string;
}

export async function addMemory(
  input: AddMemoryInput,
  accessToken: string,
): Promise<MemoryEntry> {
  const date =
    input.date ??
    new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  return apiFetch<MemoryEntry>('/eve/memories', {
    method: 'POST',
    accessToken,
    json: { fact: input.fact, date, ts: Date.now() },
  });
}

export async function deleteMemory(id: number | string, accessToken: string): Promise<void> {
  await apiFetch(`/eve/memories/${encodeURIComponent(String(id))}`, {
    method: 'DELETE',
    accessToken,
  });
}

export async function clearMemories(accessToken: string): Promise<void> {
  await apiFetch('/eve/memories', { method: 'DELETE', accessToken });
}

// ── RAS-Rules (user-spezifisch) ──────────────────────────────────────────────

export async function listRasRules(accessToken: string, limit = 200): Promise<RasRule[]> {
  return apiFetch<RasRule[]>(`/eve/ras?limit=${limit}`, { accessToken });
}

export interface SaveRasRuleInput {
  rule: string;
  trigger_context: string;
}

export async function saveRasRule(
  input: SaveRasRuleInput,
  accessToken: string,
): Promise<RasRule> {
  return apiFetch<RasRule>('/eve/ras', {
    method: 'POST',
    accessToken,
    json: { ...input, is_active: true },
  });
}

export async function deleteRasRule(id: number | string, accessToken: string): Promise<void> {
  await apiFetch(`/eve/ras/${encodeURIComponent(String(id))}`, {
    method: 'DELETE',
    accessToken,
  });
}

export async function clearRasRules(accessToken: string): Promise<void> {
  await apiFetch('/eve/ras', { method: 'DELETE', accessToken });
}

// ── Global RAS-Rules (Owner-only writes, alle lesen) ─────────────────────────

export async function listGlobalRasRules(accessToken: string): Promise<GlobalRasRule[]> {
  return apiFetch<GlobalRasRule[]>('/eve/ras/global', { accessToken });
}

export interface SaveGlobalRasRuleInput {
  rule: string;
  trigger_context?: string;
  rule_key?: string;
  category?: string;
  priority?: number;
}

export async function saveGlobalRasRule(
  input: SaveGlobalRasRuleInput,
  accessToken: string,
): Promise<GlobalRasRule> {
  return apiFetch<GlobalRasRule>('/eve/ras/global', {
    method: 'POST',
    accessToken,
    json: input,
  });
}

export async function deleteGlobalRasRule(
  id: number | string,
  accessToken: string,
): Promise<void> {
  await apiFetch(`/eve/ras/global/${encodeURIComponent(String(id))}`, {
    method: 'DELETE',
    accessToken,
  });
}

// ── Memory-Extraction via Claude (über /v1/chat-Adapter) ────────────────────────────────────────

/** OpenAI-kompatible Chat-Response (Claude via Worker-Proxy). */
export interface ChatResponse {
  choices?: Array<{
    message?: { role?: string; content?: string };
  }>;
}

/**
 * Wrappt den `/v1/chat`-Aufruf für die Memory-Extraktion-Prompts. Wir geben den
 * ROHEN Text zurück — Parsing macht `parseAiExtractedRules` separat.
 */
export async function callEveLlm(
  prompt: string,
  accessToken: string,
  options: { temperature?: number; maxTokens?: number } = {},
): Promise<string | null> {
  const data = await apiFetch<ChatResponse>('/v1/chat', {
    method: 'POST',
    accessToken,
    json: {
      model: 'claude-sonnet-4-6',
      messages: [{ role: 'user', content: prompt }],
      temperature: options.temperature ?? 0.1,
      max_tokens: options.maxTokens ?? 2000,
    },
  });
  return data?.choices?.[0]?.message?.content?.trim() ?? null;
}
